import { Container, InteractionEvent } from '@tencent/alloy-renderer';
import {
    EPpGuideOrientation,
    IGuide,
} from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model.interface';
import {
    VertGuideLine,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/components/vert-guide-line';
import {
    HorzGuideLine,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/components/horz-guide-line';
import {
    IGuideLineComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/interface/guide-line.interface';
import { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { ESourceType } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import {
    IGuideLineContainer,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/interface/guide-line-container.interface';

export interface IGuideLineContainerProps {
    x: number;
    y: number;
    orient: EPpGuideOrientation;
    guideList: Array<IGuide>;
    isUiComponent: boolean;
    type: ESourceType;
    display: boolean;
    canEdit: boolean;
    updateGuide(e: InteractionEvent, guide: IGuide, vertLineComponent: IGuideLineComponent);
    handleContextMenu(e: InteractionEvent, guide: IGuide);
}

/**
 * 水平/垂直参考线容器
 */
export class GuideLineContainer extends Container implements IGuideLineContainer {
    public componentMap: Map<IGuide, VertGuideLine | HorzGuideLine> = new Map();
    protected props: IGuideLineContainerProps;
    protected guidesManager?: IGuidesManager;
    protected type: ESourceType;

    public constructor(props: IGuideLineContainerProps) {
        super();
        this.props = props;
        const { x, y, type } = props;
        this.x = x;
        this.y = y;
        this.type = type;
        this.guidesManager = this.getAppService().editableServiceLayer?.guidesManager;
        this.initComponentMap();
        this.renderComponent();
    }

    public initComponentMap = () => {
        let guideOperationService;
        switch (this.type) {
            case ESourceType.slide:
                guideOperationService = this.getSlideService();
                break;
            case ESourceType.master:
                guideOperationService = this.getMasterService();
                break;
            case ESourceType.layout:
                guideOperationService = this.getLayoutService();
                break;
        }
        if (guideOperationService) {
            if (this.props.orient === EPpGuideOrientation.ppHorizontalGuide) {
                guideOperationService.horzUiComponentContainer = this;
            } else {
                guideOperationService.vertUiComponentContainer = this;
            }
        }
    };

    public getSlideService = () => {
        if (this.guidesManager && this.props.isUiComponent) {
            return this.guidesManager.slideGuidesService;
        }
    };

    public getMasterService = () => {
        if (this.guidesManager && this.props.isUiComponent) {
            return this.guidesManager.masterGuidesService;
        }
    };

    public getLayoutService = () => {
        if (this.guidesManager && this.props.isUiComponent) {
            return this.guidesManager.layoutGuidesService;
        }
    };

    public dispose() {
        super.dispose();
    }

    public updateProps = (props: IGuideLineContainerProps) => {
        Object.assign(this.props, props);
        this.x = props.x;
        this.y = props.y;
        requestAnimationFrame(() => {
            this.renderComponent();
        });
    };

    protected showSlideGuides = (): boolean => {
        if (this.type !== ESourceType.slide) return true;

        const masterLayoutEditService = this.getAppService().editableServiceLayer?.masterLayoutEditService;
        return masterLayoutEditService?.editType !== EDIT_TYPE.MASTER_LAYOUT;
    };
    protected canEditMasterOrLayout = (isMaster: boolean): boolean => {
        if (this.props.isUiComponent) {
            return false;
        }

        const masterLayoutEditService = this.getAppService().editableServiceLayer?.masterLayoutEditService;
        const { selectionService } = this.getAppService().viewableServiceLayer;
        if (!masterLayoutEditService) {
            return false;
        }

        if (masterLayoutEditService.editType === EDIT_TYPE.SLIDE) {
            return false;
        }
        const currentRelationModel = isMaster
            ? selectionService.currentSlide?.getRelationMasterModel()
            : selectionService.currentSlide?.getRelationLayoutModel();
        return currentRelationModel === selectionService.currentSlide;
    };

    protected renderComponent = () => {
        this.removeAllComponent();
        if (!this.needRender()) return;
        if (this.props.orient === EPpGuideOrientation.ppHorizontalGuide) {
            this.renderHorzLine();
        } else {
            this.renderVertLine();
        }
    };

    protected needRender = () => {
        if (this.getAppService().editableServiceLayer?.historyManager?.isShowHistoryPanel) {
            return false;
        }
        const masterLayoutEditService = this.getAppService().editableServiceLayer?.masterLayoutEditService;
        if (this.type === ESourceType.slide && masterLayoutEditService?.editType !== EDIT_TYPE.SLIDE) {
            return false;
        }
        return this.props.display;
    };

    protected renderVertLine = () => {
        this.props.guideList.forEach((guide) => {
            const component = this.componentMap.get(guide);
            if (!component) {
                const { pos, color } = guide;
                const vertLineComponent = new VertGuideLine({
                    position: pos,
                    color,
                    isUiComponent: this.props.isUiComponent,
                    canEdit: this.props.canEdit,
                    showGuides: this.showSlideGuides(),
                    updateGuide: (e) => {
                        this.props.updateGuide(e, guide, vertLineComponent);
                    },
                    handleContextMenu: (e) => {
                        this.props.handleContextMenu(e, guide);
                    },
                });
                this.addChild(vertLineComponent);
                this.componentMap.set(guide, vertLineComponent);
            }
        });
    };

    protected removeAllComponent = () => {
        this.componentMap.forEach((value, key) => {
            this.removeChild(value);
        });
        this.componentMap.clear();
    };

    protected renderHorzLine = () => {
        this.props.guideList.forEach((guide) => {
            const component = this.componentMap.get(guide);
            if (!component) {
                const { pos, color } = guide;
                const horzLineComponent = new HorzGuideLine({
                    position: pos,
                    color,
                    isUiComponent: this.props.isUiComponent,
                    canEdit: this.props.canEdit,
                    showGuides: this.showSlideGuides(),
                    updateGuide: (e) => {
                        this.props.updateGuide(e, guide, horzLineComponent);
                    },
                    handleContextMenu: (e) => {
                        this.props.handleContextMenu(e, guide);
                    },
                });
                this.addChild(horzLineComponent);
                this.componentMap.set(guide, horzLineComponent);
            }
        });
    };

    protected getAppService = () => AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
}
